import React, { useEffect, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./board-settings-page.scss";
import { BoardService } from "./board-service";
import ConfirmDialog from "./confirm-dialog";
import CustomFieldModal from "./custom-field-modal";
import { CustomFieldDef, CustomFieldType, ISSUE_TYPE_LABELS, IssueType, Project } from "./types";
import { useBoardConfig } from "./use-board";

interface ProjectSettingsProps {
  project: Project;
  // Called after the project is deleted so the hub can navigate back to the index.
  onDeleted: () => void;
}

const TYPE_LABEL: Record<CustomFieldType, string> = {
  text: "Text",
  number: "Number",
  date: "Date",
  select: "Select",
  checkbox: "Checkbox",
  user: "User"
};

function reorderIds(defs: CustomFieldDef[], index: number, direction: -1 | 1): string[] {
  const ids: string[] = defs.map((def: CustomFieldDef) => def.id);
  const target: number = index + direction;
  if (target < 0 || target >= ids.length) return ids;
  const next: string[] = [...ids];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

// The issue types a field is scoped to — "All types" when unscoped (the default).
function appliesToLabel(def: CustomFieldDef): string {
  if (def.issueTypes.length === 0) return "All types";
  return def.issueTypes.map((entry: IssueType) => ISSUE_TYPE_LABELS[entry]).join(", ");
}

// Project settings (the hub's Settings tab): rename / delete the project, and manage custom field
// definitions. Custom fields are project-scoped — each field belongs to this project and can be
// scoped further to specific issue types.
const ProjectSettings: React.FC<ProjectSettingsProps> = ({ project, onDeleted }) => {
  const { config } = useBoardConfig();
  const canManage: boolean = config.canManage;
  const [name, setName] = useState<string>(project.name);
  const [addFieldOpen, setAddFieldOpen] = useState<boolean>(false);
  const [editField, setEditField] = useState<CustomFieldDef | null>(null);
  const [pendingDeleteField, setPendingDeleteField] = useState<CustomFieldDef | null>(null);
  const [confirmDeleteProject, setConfirmDeleteProject] = useState<boolean>(false);

  // Custom fields are project-scoped — only this project's fields.
  const projectFields: CustomFieldDef[] = config.customFieldDefs.filter(
    (def: CustomFieldDef) => def.projectKey === project.key
  );

  useEffect((): void => {
    setName(project.name);
  }, [project.name]);

  const trimmedName: string = name.trim();
  const canRename: boolean = trimmedName.length > 0 && trimmedName !== project.name;

  return (
    <div className="kuika-board-settings">
      {!canManage && (
        <div className="kuika-board-settings__readonly-note">
          <KuikaSvg name="lock" width={13} height={13} />
          View only — workspace owners or supervisors can change project settings.
        </div>
      )}

      <section className="kuika-board-settings__section">
        <div className="kuika-board-settings__section-title">Project</div>
        {canManage ? (
          <>
            <div className="kuika-board-settings__section-sub">
              Rename this project, or remove it. Deleting removes its issues, statuses, sprints, versions and boards.
            </div>
            <div className="kuika-board-settings__rename">
              <KuikaInput value={name} onChange={(_event, value: string | number) => setName(String(value))} />
              <KuikaButton
                type="primary"
                onClick={() => {
                  if (canRename) void BoardService.renameProject(project.key, trimmedName);
                }}
                disabled={!canRename}
              >
                Rename
              </KuikaButton>
            </div>
            <button
              type="button"
              className="kuika-board-settings__danger-link"
              onClick={() => setConfirmDeleteProject(true)}
            >
              Delete project
            </button>
          </>
        ) : (
          <div className="kuika-board-settings__section-sub">{project.name}</div>
        )}
      </section>

      <section className="kuika-board-settings__section">
        <div className="kuika-board-settings__section-head">
          <div>
            <div className="kuika-board-settings__section-title">Custom fields</div>
            <div className="kuika-board-settings__section-sub">
              Extra properties shown on this project's issues.{canManage ? " Use the arrows to reorder them." : ""}
            </div>
          </div>
          {canManage && (
            <KuikaButton type="secondary" size="sm" onClick={() => setAddFieldOpen(true)}>
              Add field
            </KuikaButton>
          )}
        </div>

        {projectFields.length === 0 ? (
          <div className="kuika-board-settings__empty">No custom fields yet.</div>
        ) : (
          <ul className="kuika-board-settings__list">
            {projectFields.map((def: CustomFieldDef, index: number) => (
              <li key={def.id} className="kuika-board-settings__row">
                {canManage && (
                  <span className="kuika-board-settings__reorder">
                    <button
                      type="button"
                      className="kuika-board-settings__move"
                      disabled={index === 0}
                      onClick={() =>
                        BoardService.reorderCustomFields(project.key, reorderIds(projectFields, index, -1))
                      }
                      aria-label={`Move ${def.name} up`}
                      title="Move up"
                    >
                      <KuikaSvg name="chevron-up" type="arrows" width={11} height={11} />
                    </button>
                    <button
                      type="button"
                      className="kuika-board-settings__move"
                      disabled={index === projectFields.length - 1}
                      onClick={() => BoardService.reorderCustomFields(project.key, reorderIds(projectFields, index, 1))}
                      aria-label={`Move ${def.name} down`}
                      title="Move down"
                    >
                      <KuikaSvg name="chevron-down" type="arrows" width={11} height={11} />
                    </button>
                  </span>
                )}
                <span className="kuika-board-settings__row-name">{def.name}</span>
                <span className="kuika-board-settings__row-applies" title={appliesToLabel(def)}>
                  {appliesToLabel(def)}
                </span>
                <span className="kuika-board-settings__row-type">{TYPE_LABEL[def.type] ?? def.type}</span>
                {canManage && (
                  <>
                    <button
                      type="button"
                      className="kuika-board-settings__row-edit"
                      onClick={() => setEditField(def)}
                      aria-label={`Edit custom field ${def.name}`}
                      title="Edit field"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="kuika-board-settings__row-remove"
                      onClick={() => setPendingDeleteField(def)}
                      aria-label={`Delete custom field ${def.name}`}
                      title="Delete field"
                    >
                      <KuikaSvg name="trash" width={15} height={15} />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <CustomFieldModal
        open={addFieldOpen || editField !== null}
        existing={editField ?? undefined}
        onClose={() => {
          setAddFieldOpen(false);
          setEditField(null);
        }}
        onSubmit={(fieldName: string, type: CustomFieldType, options: string[], issueTypes: IssueType[]) => {
          if (editField) void BoardService.updateCustomField(editField.id, fieldName, options, issueTypes);
          else void BoardService.createCustomField(project.key, fieldName, type, options, issueTypes);
        }}
      />

      <ConfirmDialog
        open={pendingDeleteField !== null}
        title="Delete custom field?"
        message={`"${pendingDeleteField?.name ?? ""}" and its values will be removed from every issue. This can't be undone.`}
        confirmLabel="Delete"
        cancelLabel="Keep"
        danger
        onConfirm={() => {
          if (pendingDeleteField) void BoardService.deleteCustomField(pendingDeleteField.id);
          setPendingDeleteField(null);
        }}
        onCancel={() => setPendingDeleteField(null)}
      />

      <ConfirmDialog
        open={confirmDeleteProject}
        title="Delete project?"
        message={`"${project.name}" and all its issues, statuses, sprints, versions and boards will be permanently removed. This can't be undone.`}
        confirmLabel="Delete project"
        cancelLabel="Keep"
        danger
        onConfirm={() => {
          void BoardService.deleteProject(project.key);
          setConfirmDeleteProject(false);
          onDeleted();
        }}
        onCancel={() => setConfirmDeleteProject(false)}
      />
    </div>
  );
};

export default ProjectSettings;
